import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from 'react-apollo';

import { PrintProductConfiguratorModal } from '../../../../../../../../../services/prints/components/PrintProductConfigurator';
import PrintProductGroup from './PrintProductGroup';
import { PrintProductSizesModal } from './components/PrintProductSizes';
import { getPreferredPrintProductSizeOrientation } from './helpers';
import PrintProductSizeRoesConfiguratorModal from './components/PrintProductSizeRoesConfigurator';
import { printProductSizeBaseInfo } from '../../../../../../../../../graphql/queries/print-product';
import { getPrintProductSizesForPackagesAndPricing } from '../../../../../../AlbumFolder/AlbumFolders/AlbumFolderPictureDay/PrintPackageForm/helpers';

const propTypes = {
  printProducts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  readOnly: PropTypes.bool,
  renderPrintProduct: PropTypes.func,
  onCartItemToggle: PropTypes.func.isRequired,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string,
  }),
  getPrintProductSizePrice: PropTypes.func,
  disabled: PropTypes.bool,
  disabledElement: PropTypes.node,
  getCartItem: PropTypes.func,
  isAllPhotos: PropTypes.bool,
  printBackgrounds: PropTypes.array,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const PrintProductsGroup = ({
  printProducts,
  photo,
  onCartItemToggle,
  renderPrintProduct,
  readOnly,
  albumMembershipGroup,
  identity,
  getPrintProductSizePrice,
  disabled,
  disabledElement,
  getCartItem,
  isAllPhotos = false,
  printBackgrounds,
  watermarkSettings,
}) => {
  const client = useApolloClient();
  const [configuring, setConfiguring] = useState(false);
  const [selectedPrintProduct, setSelectedPrintProduct] = useState(null);
  const [selectedPrintProductSize, setSelectedPrintProductSize] =
    useState(null);

  const onPrintSizeSelect = async (printProduct, printProductSize) => {
    setSelectedPrintProduct(printProduct);
    const { data: { printProducts: printProductWithSizeInfo } = {} } =
      await client.query({
        query: printProductSizeBaseInfo,
        variables: {
          print_product_id: printProduct?.id,
          print_product_size_id: printProductSize?.id,
        },
        skip: !printProduct?.id || !printProductSize?.id,
        fetchPolicy: 'cache-first',
      });
    setSelectedPrintProductSize(
      printProductWithSizeInfo?.records?.[0]?.sizes?.records?.[0] ??
        printProductSize,
    );
    setConfiguring(true);
  };

  const onConfiguratorClose = () => {
    setConfiguring(false);
    setSelectedPrintProduct(null);
    setSelectedPrintProductSize(null);
  };

  return (
    <Fragment>
      {printProducts.map(printProduct => {
        if (renderPrintProduct) {
          return renderPrintProduct({
            printProduct,
            productsTotal:
              getPrintProductSizesForPackagesAndPricing(printProduct).length,
            onPrintProductSelect: setSelectedPrintProduct,
            disabled,
            disabledElement,
          });
        }

        return (
          <PrintProductGroup
            key={printProduct.id}
            printProduct={printProduct}
            onPrintProductSelect={() => setSelectedPrintProduct(printProduct)}
            onPrintProductSizeSelected={printProductSize => {
              if (
                getCartItem?.({
                  printProductId: printProduct.id,
                  printProductSizeId: printProductSize.id,
                  photoId: photo?.id,
                })
              ) {
                onCartItemToggle({
                  printProduct,
                  printProductSize,
                });
              } else {
                onPrintSizeSelect(printProduct, printProductSize);
              }
            }}
            readOnly={readOnly}
            getPrintProductSizePrice={getPrintProductSizePrice}
            disabled={disabled}
            disabledElement={disabledElement}
            hasIndividualSizeCartItem={
              printProduct.sizes?.total === 1 &&
              getCartItem?.({
                printProductId: printProduct.id,
                printProductSizeId: printProduct.sizes.records[0].id,
                photoId: photo?.id,
              })
            }
          />
        );
      })}
      {selectedPrintProduct && !selectedPrintProductSize && (
        <PrintProductSizesModal
          forceOpen
          photo={photo}
          onClose={() => setSelectedPrintProduct(null)}
          printProductId={selectedPrintProduct.id}
          printProduct={selectedPrintProduct}
          onPrintProductSizeSelect={printProductSize => {
            if (
              getCartItem?.({
                printProductId: selectedPrintProduct.id,
                printProductSizeId: printProductSize.id,
                photoId: photo?.id,
              })
            ) {
              onCartItemToggle({
                printProduct: selectedPrintProduct,
                printProductSize,
              });
            } else {
              onPrintSizeSelect(selectedPrintProduct, printProductSize);
            }
          }}
          getPrintProductSizePrice={getPrintProductSizePrice}
          getCartItem={getCartItem}
        />
      )}

      {configuring && photo && selectedPrintProduct?.card && (
        <PrintProductConfiguratorModal
          forceOpen
          printProductId={selectedPrintProduct.id}
          printProductSizeId={selectedPrintProductSize.id}
          availablePrintProductSizeIds={(
            selectedPrintProduct.sizes?.records ?? []
          ).map(printProductSize => printProductSize.id)}
          controlsDisabled
          photo={photo}
          photoId={photo.id}
          orientation={getPreferredPrintProductSizeOrientation({
            printProductSize: selectedPrintProductSize,
            photo,
          })}
          onClose={() => {
            onConfiguratorClose();
          }}
          onComplete={({
            accountCard,
            albumCustomPrint,
            printProduct,
            printProductSize,
          }) => {
            onCartItemToggle({
              printProduct: printProduct ?? selectedPrintProduct,
              printProductSize: printProductSize ?? selectedPrintProductSize,
              accountCard,
              albumCustomPrint,
            });

            onConfiguratorClose();
          }}
          albumMembershipGroup={albumMembershipGroup}
          identity={identity}
          printBackgrounds={printBackgrounds}
          watermarkSettings={watermarkSettings}
        />
      )}
      {configuring && photo && !selectedPrintProduct?.card && (
        <PrintProductSizeRoesConfiguratorModal
          forceOpen
          printProduct={selectedPrintProduct}
          printProductSize={selectedPrintProductSize}
          photo={photo}
          identity={identity}
          albumMembershipGroup={albumMembershipGroup}
          isAllPhotos={isAllPhotos}
          onComplete={({
            printProduct,
            printProductSize,
            roesConfiguration,
          }) => {
            onCartItemToggle({
              printProduct: printProduct ?? selectedPrintProduct,
              printProductSize: printProductSize ?? selectedPrintProductSize,
              roesConfiguration,
            });

            onConfiguratorClose();
          }}
          onClose={() => {
            onConfiguratorClose();
          }}
        />
      )}
    </Fragment>
  );
};

PrintProductsGroup.propTypes = propTypes;

export default PrintProductsGroup;
